import {
  type InfiniteData,
  type UseInfiniteQueryOptions,
  keepPreviousData,
  useInfiniteQuery,
} from '@tanstack/react-query';
import {
  type CommentResolutionFilterOption,
  type CommentVisibilityFilterOption,
  type GetNodeCommentsResponse,
} from '@/types/comments';
import { useAuthenticatedQueryFn } from '@/hooks/useAuthenticatedQuery';
import { getNodeComments } from '@/services/comment.service';
import { getNextPageParam } from '@/lib/pagination';
import { QueryKey } from '@/lib/queryKeys';

export interface CommentFilters {
  sortOrder: string;
  sortValue: string;
  resolution?: CommentResolutionFilterOption;
  visibility?: CommentVisibilityFilterOption;
  selected?: string;
}

export const nodeCommentListQueryKey = (
  nodeId: string | undefined,
  shareId: string | undefined,
  filters: CommentFilters,
) =>
  [
    QueryKey.NodeCommentList,
    nodeId,
    shareId,
    filters.sortOrder,
    filters.sortValue,
    filters.resolution,
    filters.visibility,
    filters.selected,
  ] as const;

type NodeCommentListQueryKey = ReturnType<typeof nodeCommentListQueryKey>;

export const isShareCommentListQuery = (
  queryKey: ReadonlyArray<unknown>,
  shareId: string,
): queryKey is NodeCommentListQueryKey => {
  const [key, , queryShareId] = queryKey;

  return key === QueryKey.NodeCommentList && queryShareId === shareId;
};

export const useNodeCommentList = (
  nodeId: string | undefined,
  params: {
    pagination: { initialLimit: number; nextPageLimit: number; offset: number };
    sort: { order: string; value: string };
    filter: { resolution?: CommentResolutionFilterOption; visibility?: CommentVisibilityFilterOption };
    selected?: string;
    shareId?: string;
  },
  options?: Omit<
    UseInfiniteQueryOptions<GetNodeCommentsResponse, Error, InfiniteData<GetNodeCommentsResponse>>,
    'queryKey' | 'queryFn' | 'initialPageParam' | 'getNextPageParam'
  >,
) => {
  const nodeCommentsQueryFn = useAuthenticatedQueryFn(getNodeComments);

  const { data, isPending, isFetching, error, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery<
    GetNodeCommentsResponse,
    Error,
    InfiniteData<GetNodeCommentsResponse>
  >({
    meta: { suppressForbiddenRedirect: true },
    queryKey: nodeCommentListQueryKey(nodeId, params.shareId, {
      sortOrder: params.sort.order,
      sortValue: params.sort.value,
      resolution: params.filter.resolution,
      visibility: params.filter.visibility,
      selected: params.selected,
    }),
    initialPageParam: params.pagination.offset,
    queryFn: ({ pageParam }) =>
      nodeCommentsQueryFn({
        nodeId: nodeId as string,
        limit: params.pagination.initialLimit,
        offset: pageParam as number,
        sortBy: params.sort.value,
        sortOrder: params.sort.order,
        filter: params.filter,
        selected: params.selected,
        shareId: params.shareId,
      }),
    getNextPageParam: (lastPage) => getNextPageParam(lastPage, params.pagination.nextPageLimit),
    placeholderData: keepPreviousData,

    enabled: Boolean(nodeId),
    refetchOnMount: true,
    ...options,
  });

  const comments = data?.pages?.flatMap((page) => page.comments) ?? [];
  const lastPage = data?.pages?.[data.pages.length - 1];
  const pagination = lastPage?.pagination;

  return {
    data: { comments, pagination },
    commentsArePending: isPending,
    commentsAreFetching: isFetching,
    commentsError: error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  };
};
